/**
 * `pnpm dev:mock` 과 데모 빌드(`vite build --mode demo`)의 진입점. vite.config.ts 의
 * mockApi·demoBuild 플러그인이 index.html 의 main.tsx 자리에 이 파일을 넣는다 — 앱 코드는
 * mock 을 모르고, 일반 prod 빌드는 이 파일을 지나가지 않으므로 번들에도 소스맵에도 남지 않는다.
 *
 * worker 가 준비되기 전에 앱이 요청을 보내면 그 요청은 mock 을 비켜 간다. 그래서
 * start 를 기다린 뒤에 앱을 불러온다. top-level await 는 vite 의 기본 빌드 대상이 받지
 * 않으므로 함수로 감싼다.
 */
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import DevFloatingTool from "./DevFloatingTool";
import { worker } from "./browser";
import { db } from "./db";
import { scenarios } from "./scenarios";
import { MOCK_GOOGLE_USER_ID, MOCK_SESSION_COOKIE } from "./session";

async function start() {
  await worker.start({
    /**
     * handler 가 없는 API 요청을 **콘솔에 error 로 남긴다.** "bypass" 로 두면 그 요청이
     * 조용히 vite 프록시를 타고 진짜 백엔드로 가, 화면이 목만으로 도는지가 백엔드가
     * 떠 있느냐에 따라 달라진다 — 목이 모자라는 것을 영영 모른다.
     *
     * 이 설정은 요청을 막지는 못한다(service worker 경로에서는 throw 가 삼켜진다).
     * 요청을 끊는 것은 vite 쪽 `/api` 미들웨어가 하고, 여기서는 어느 요청이었는지를
     * 콘솔에 남긴다. → vite.config.ts
     *
     * 화면 이동(로그아웃·Google 로그인)은 vite 미들웨어가 받으니 건드리지 않고,
     * 모듈·HMR·정적 파일도 그대로 통과시킨다.
     */
    onUnhandledRequest(request, print) {
      if (request.destination === "document") return;
      const { pathname } = new URL(request.url);
      if (!pathname.startsWith("/api/") && !pathname.startsWith("/accounts/")) return;
      print.error();
    },
  });

  // `?scenario=이름` — 시드와 다른 상황(빈 목록·서버 오류)을 기본 handler 위에 얹는다. → ./scenarios.ts
  const scenario = new URLSearchParams(location.search).get("scenario");
  if (scenario) {
    if (scenario in scenarios) worker.use(...scenarios[scenario]);
    else console.error(`[mock] 없는 scenario: ${scenario} — src/mocks/scenarios.ts`);
  }

  // 데모는 처음 들어오면 로그인 화면 없이 관리자로 들어온다 — 세션 쿠키가 아예 없을 때만이다.
  // 로그아웃은 데모 nginx 가 쿠키를 빈 값으로 남기므로 로그인 화면이 그대로 뜬다.
  const firstVisit = !new RegExp(`(?:^|; )${MOCK_SESSION_COOKIE}=`).test(document.cookie);
  if (import.meta.env.MODE === "demo" && firstVisit) db.signIn(MOCK_GOOGLE_USER_ID);

  await import("@/main");

  // 개발용 도구는 앱과 다른 루트에 띄운다 — 앱 트리 안에 mock 을 끼우지 않는다.
  // 자동화 브라우저(ui-snapshots 캡처)에서는 화면을 가리지 않게 띄우지 않는다.
  if (!navigator.webdriver) {
    const devToolRoot = document.createElement("div");
    document.body.append(devToolRoot);
    createRoot(devToolRoot).render(createElement(DevFloatingTool));
  }
}

void start();
